import DataElement, { DataElementOptions } from "./DataElement";
import apifetch from "../utils/apifetch";
import { ref, unref, computed, Ref, ComputedRef } from "vue";
import WindowManager from "../utils/WindowManager"
import XMLRDBNavigation from "../xmlrdb-metadata/XMLRDBNavigation";
import XMLRDBElement from "../xmlrdb-metadata/Element";
import { TransferDataConverter, VueFormsXMLRDBConverter } from "./TransferDataConverter";


export interface Permissions {
  canEdit?: boolean;
}

export interface RootElementOptions extends DataElementOptions {
  url: string;
  savePath?: string;
  permissions?: Permissions;
  rootMetadata: XMLRDBNavigation;
}

export default class RootElement extends DataElement{

  #isEditable = ref(false);
  url: string;
  savePath: string;
  permissions: Permissions;
  canEdit: ComputedRef<boolean>;
  isLoading: Ref<boolean>;
  rootMetadata: XMLRDBNavigation;

  beforeWriteHandler = new Map<XMLRDBElement, (dataElement: DataElement) => boolean>();

  transferDataConverter: TransferDataConverter;

  constructor(options: RootElementOptions, transferDataConverter: TransferDataConverter = new VueFormsXMLRDBConverter()) {
    super({ depth: 0, nr: 0, ...options });
    this.url = options.url;
    this.savePath = options.savePath ?? "";
    this.permissions = options.permissions || {};
    this.transferDataConverter = transferDataConverter;
    this.canEdit = computed(() => {
      if (this.permissions.canEdit) {
        return unref(this.permissions.canEdit);
      }
      else {
        return true;
      }
    });
    this.isEditable = computed(() => {
      return this.canEdit.value && this.#isEditable.value;
    });

    this.isLoading = ref(false);
    /** @type {import("../xmlrdb-metadata/XMLRDBNavigation").default} */
    this.rootMetadata = options.rootMetadata;
    /**
     beforeWriteHandler werden aufgerufen, bevor XML zu einem DataElement erzeugt wird.

     beforeWriteHandler ist eine Map
       xmlrdb-metadata/Element -> Function

     Function erhält als Parameter ein xmlrdb-data/DataElement und wird aufgerufen, bevor aus diesem DataElement ein Änderungs-XML geschrieben wird.
     Wenn die registrierte Function false zurück liefert, dann wird für das Element KEIN XML ERZEUGT.
     Es reicht nicht aus "falsy" zurück zu liefern. So wird auch bei Functions ohne Rückgabewerte XML erzeugt.
     
     Anwendungsbeispiel:
       async load() {
         const meta = await fetchXML2Metadata("R2X","MA.Ausgabenpositionen.xml");
         const xmlrdbNav = await this.loadData(meta);         
         xmlrdbNav.beforeWriteHandler.set( meta.getElementsByName("MIP_MEINEKLASSE")[0], (dataElement)=>
           if (dataElement.$.NETTOKOSTEN_EURO === null) return false;                  // Keine Nettokosten angegeben -> Nicht speichern
           dataElement.$.BRUTTOKOSTEN_EURO === dataElement.$.NETTOKOSTEN_EURO * 1.19;  // Nettokosten in Bruttokosten umrechnen
         })     
       }
     */
  }  

  setSavePath(savePath: string) {
    this.savePath = savePath;
  }

  setEditable(bool: boolean) {
    this.#isEditable.value = bool;
  }

  getEditable() {
    return this.#isEditable.value;
  }

  path() {
	return [];
  }

  /**
   * Rückgabe: Sequences
   * {
   *   sequences {
   *     EF_PROJEKT: 4711,
   *     EF_FOERDERANTRAG: 815,
   *   }
   * }
   */
  async _save(savePath?: string) {
    await this.dispatch("beforeSave");
    let string = await this.transferDataConverter.convertAllToText(this);
    let response = null;
    let writePathOptions = {
      root: this,
      savePath: savePath ?? this.savePath,
      dataElement: this
    };

    if (this.onChangeReenterPassword.value) {
      const writePWDPath = this.transferDataConverter.getWritePWDPath(writePathOptions);
      let fd = new FormData();
      fd.set("xml", string);
      let pwd = await WindowManager.enterPassword(undefined, "Passwort eingeben");
      if (pwd === false)
        throw "Abbruch durch Nutzer";

      fd.set("pwd", pwd)
      response = await apifetch(writePWDPath, {
        method: "POST",
        body: fd
      });
    }
    else {
      response = await apifetch(this.transferDataConverter.getWritePath(writePathOptions), {
        method: "POST",
        headers: { 'Content-Type': this.transferDataConverter.mimeType },
        body: string
      });
    }
    const res = await this.transferDataConverter.response2JSON(this, response);
    await this.dispatch("saved", res);
    return true;
  }

  async save(savePath?: string) { // savePath mit '/' und ohne '.xslt'-Endung, z.B. '/VW.Wertliste'
    this.isLoading.value = true;
    try {
      await this._save(savePath);
    }
    finally { this.isLoading.value = false; }
  }

  /**
   * Rückgabe: Sequences
   */
  async saveDataElement(dataElement: DataElement, savePath?: string) { // savePath mit '/' und ohne '.xslt'-Endung, z.B. '/VW.Wertliste'
    this.isLoading.value = true;
    let writePathOptions = {
      root: this,
      savePath: savePath ?? this.savePath,
      dataElement: dataElement
    };
    let res = null;
    try{
      await this.dispatch("beforeSave");
      let string = await this.transferDataConverter.convertElementToText(dataElement);
      let response = null;
      if (dataElement.onChangeReenterPassword.value) {
        const writePWDPath = this.transferDataConverter.getWritePWDPath(writePathOptions);
        let fd = new FormData();
        fd.set("xml", string);
        let pwd = await WindowManager.enterPassword(undefined, "Passwort eingeben");
        if (pwd === false)
          throw "Abbruch durch Nutzer";

        fd.set("pwd", pwd)
        response = await apifetch(writePWDPath, {
          method: "POST",
          body: fd
        });
      }
      else {
        response = await apifetch(this.transferDataConverter.getWritePath(writePathOptions), {
          method: "POST",
          headers: { 'Content-Type': this.transferDataConverter.mimeType },
          body: string
        });
      }
      res = await this.transferDataConverter.response2JSON(this, response);
      await this.dispatch("saved", res);
    }
    finally { this.isLoading.value = false; }
    return res;
  }

  /**
   * Rückgabe: Sequences
   */
   async saveAndReload (savePath?: string) { // savePath mit '/' und ohne '.xslt'-Endung, z.B. '/VW.Wertliste'
    this.isLoading.value = true;
    try{
      if(await this._save(savePath))
        await this._reload();
    }
    finally { this.isLoading.value = false; }
  }

  /*
    Falls im Formular Änderungenv orgenommen wurden, fragt diese Funktion, ob gespeichert werden soll.
    Falls der Nutzer das Speichern anforert, wird gespeichert.
    Rückgaben:
    # -1: Keine Änderung vorgenommen
    #  0: Änderung vorgenommen und Nutzer will nicht speichern
    #  1: Änderung vorgenommen und gespeichert
   */
  async saveAskIfChanged(message: string | null = null) {

    if (this.hasChanged.value) {
      let dialogResponse = true
      dialogResponse = await this.askProceedIfChanged(message);
      if (!dialogResponse) {
        return 0;
      }
      else {
        await this.save();
        return 1;
      }
    }
    else {
      return -1;
    }
  }

  async _reload() {
    const newXML = await apifetch(this.url);
    const dataxml = await newXML.text();
    this.transferDataConverter.updateViewModel(dataxml, this, newXML.headers);
    this.dispatch("afterLoad", this);
  }


  async askProceedIfChanged(message: string | null = null, {yesMsg = "Daten neu laden & Änderungen verwerfen", noMsg = "Abbrechen"} = {}) {
    if (this.hasChanged.value) {
      let yes = true;
      // Wenn die Fehlermeldung "Das aktuelle Formular hat ungespeicherte Änderungen" kommt ohne dass man weiß warum, dann kommentiert man die folgende Zeile
      // aus. Dann wird auf die Console das XML geschrieben.
      // this.convertToXML();
      yes = await WindowManager.queryByChoice([[yesMsg, true], [noMsg, false]],
                                              message ?? "<p>Das aktuelle Formular hat ungespeicherte Änderungen. Wenn Sie fortfahren gehen diese Änderungen verloren.</p><p>Trotzdem fortfahren?</p>",
                                              "Ungespeicherte Daten verwerfen?");
      return yes;
    }
    return true;
  }
  

  async reset(force = false) {
    if ( !force && !await this.askProceedIfChanged()) {
      return false;
    }
    try {
      this.isLoading.value = true;
      await this._reload()
    }
    catch (e) {
      console.error(e);
    }
    finally { this.isLoading.value = false; }
    return true;
  }


}